import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FiHeart, FiEye, FiShare2, FiTrash2, FiStar, FiImage } from 'react-icons/fi';
import toast from 'react-hot-toast';
import userApi from '../../api/userApi';
import { DEFAULT_PRODUCT_IMAGE } from '../../utils/constants';

const Wishlist = () => {
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setIsLoading(true);
        const data = await userApi.getWishlist();
        
        if (!Array.isArray(data)) {
          console.error('Unexpected API response format:', data);
          toast.error('Error loading wishlist: Invalid data format');
          return;
        }

        // Transform API data to match the expected format
        const transformedWishlist = data.map(item => ({
          id: item.product?.id,
          name: item.product?.name,
          price: parseFloat(item.product?.price),
          // Use final_price if available, otherwise fall back to sale_price or regular price
          finalPrice: parseFloat(item.product?.final_price || item.product?.sale_price || item.product?.price),
          image: item.product?.variants?.[0]?.images?.[0]?.images || '',
          color: item.product?.variants?.[0]?.color || '',
          size: item.product?.variants?.[0]?.size || '',
          // Store the offer type to correctly display the discount format
          offerType: item.product?.active_offers?.[0]?.offer_type || '',
          discount: item.product?.active_offers?.[0]?.discount_value || 0,
          inStock: item.product?.variants?.some(variant => variant.stock > 0) || false,
          category: item.product?.category?.name || '',
          brand: item.product?.brand?.name || ''
        }));

        setWishlistItems(transformedWishlist);
      } catch (error) {
        if (error.message.includes('login')) {
          navigate('/login');
          return;
        }
        console.error('Error fetching wishlist:', error);
        toast.error('Failed to load wishlist');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWishlist();
  }, [navigate]);

  const removeFromWishlist = async (productId) => {
    try {
      setIsDeleting(true);
      
      // Store the current items before deletion
      const previousItems = [...wishlistItems];
      
      // Optimistically update UI
      setWishlistItems(prevItems => prevItems.filter(item => item.id !== productId));
      
      try {
        await userApi.removeFromWishlist(productId);
        toast.success('Item removed from wishlist');
        window.dispatchEvent(new Event('wishlistUpdated'));
      } catch (error) {
        // Revert the UI change if the API call failed
        setWishlistItems(previousItems);
        
        if (error.message.includes('login')) {
          navigate('/login');
          return;
        }
        
        if (error.message.includes('not found')) {
          // If item wasn't found, keep it removed from UI but show a different message
          setWishlistItems(prevItems => prevItems.filter(item => item.id !== productId));
          toast.info('Item was already removed from wishlist');
        } else {
          toast.error(error.message || 'Failed to remove item from wishlist');
        }
      }
    } catch (error) {
      console.error('Error in removeFromWishlist:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleRemoveClick = (itemId) => {
    if (!itemId) {
      toast.error('Invalid item');
      return;
    }
    setItemToDelete(itemId);
    setShowDeleteModal(true);
  };

  const confirmRemove = () => {
    if (!itemToDelete) {
      toast.error('No item selected for removal');
      setShowDeleteModal(false);
      return;
    }
    removeFromWishlist(itemToDelete);
  };

  const handleShare = async (item) => {
    try {
      await navigator.share({
        title: item.name,
        text: `Check out this amazing ${item.name} at Panchali Boutique!`,
        url: window.location.href,
      });
    } catch (err) {
      toast.error('Failed to share');
      console.error('Error sharing:', err);
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Wishlist | Panchali Boutique</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">My Wishlist</h1>
          <p className="mt-4 text-gray-600">
            {wishlistItems.length} items saved
          </p>
        </motion.div>

        {wishlistItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
            <AnimatePresence>
              {wishlistItems.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                  transition={{ duration: 0.3 }}
                  layout
                  className="bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  {/* Product Image */}
                  <div className="relative aspect-[3/4] overflow-hidden group">
                    {!failedImages.has(item.id) ? (
                      <img
                        src={item.image || DEFAULT_PRODUCT_IMAGE}
                        alt={item.name}
                        className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                        onError={() => {
                          console.error('Failed to load image:', item.image);
                          setFailedImages(prev => new Set(prev).add(item.id));
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <FiImage className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    {item.discount > 0 && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-0.5 text-xs sm:text-sm font-medium">
                        {item.offerType === 'PERCENTAGE' 
                          ? `${item.discount}% OFF` 
                          : `₹${item.discount} OFF`}
                      </div>
                    )}
                    {!item.inStock && (
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                        <span className="text-white text-sm sm:text-lg font-medium">Out of Stock</span>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="p-3 sm:p-4 md:p-6 space-y-2 sm:space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm sm:text-base font-medium text-gray-900 line-clamp-2">
                        {item.name}
                      </h3>
                    </div>
                    
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-xs sm:text-sm text-gray-500">{item.category}</span>
                      <span className="text-xs sm:text-sm text-gray-500">•</span>
                      <span className="text-xs sm:text-sm text-gray-500">{item.reviews} reviews</span>
                    </div>
                    
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm sm:text-lg font-bold text-gray-900">
                        ₹{item.finalPrice.toLocaleString()}
                      </span>
                      {item.finalPrice < item.price && (
                        <span className="text-xs sm:text-sm text-gray-500 line-through">
                          ₹{item.price.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Display discount with correct format based on offer type */}
                    {item.discount > 0 && (
                      <div className="text-xs sm:text-sm text-green-600 font-medium">
                        {item.offerType === 'PERCENTAGE' 
                          ? `${item.discount}% OFF` 
                          : `₹${item.discount} OFF`}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 sm:gap-3 pt-2 sm:pt-4">
                      <button
                        onClick={() => handleViewProduct(item.id)}
                        className="flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 text-xs sm:text-sm font-medium bg-[#2C3E50] hover:bg-[#34495E] text-white transition-all duration-300 shadow-sm hover:shadow-md active:transform active:scale-[0.98]"
                      >
                        <FiEye className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">View Product</span>
                        <span className="sm:hidden">View</span>
                      </button>
                      
                      <button
                        onClick={() => handleShare(item)}
                        className="p-1.5 sm:p-2.5 text-[#2C3E50] hover:text-[#34495E] bg-[#ECF0F1] hover:bg-[#E8E8E8] transition-all duration-300 hover:shadow-sm active:transform active:scale-[0.98]"
                        title="Share"
                      >
                        <FiShare2 className="h-3 w-3 sm:h-5 sm:w-5" />
                      </button>
                      
                      <button
                        onClick={() => handleRemoveClick(item.id)}
                        className="p-1.5 sm:p-2.5 text-[#E74C3C] hover:text-[#C0392B] bg-[#FADBD8] hover:bg-[#F5B7B1] transition-all duration-300 hover:shadow-sm active:transform active:scale-[0.98]"
                        title="Remove from wishlist"
                      >
                        <FiTrash2 className="h-3 w-3 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <FiHeart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-900 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-600 mb-8">Start adding items to your wishlist!</p>
            <Link
              to="/shop"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Continue Shopping
            </Link>
          </motion.div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Remove from Wishlist</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to remove this item from your wishlist?</p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemove}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Wishlist;



























